/**
 * StoresProvider
 * Contain most logic handing with Omnitech Api
 */
import moment from 'moment'
import React, { useContext, useMemo, useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import { CacheChecksumContext, createPersistedState } from '../../cache'
import { cancelRequest } from '../../helpers'
import StoresContext from './stores-context';
import { fetchStore, fetchStoreByCode, fetchStores, fetchStoreDateTimeInformation } from './stores-api';

// create use state hook with persisted storage
// TODO: support using different storage provider
const useStoresState = createPersistedState('stores')
const useStoresChecksumState = createPersistedState('storesChecksum')

export default function StoresProvider({ children, config, currentLocale }) {

  // handle cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('stores')

  // internal state
  const [stores, setStores] = useStoresState({})
  const [storesChecksum, setStoresChecksum] = useStoresChecksumState('')
  const [storesReady, setStoresReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  const today = moment().format('YYYY-MM-DD')
  // get cache with current locale
  const availableLocales = useMemo(() => (
    _.get(config, 'locale.availableLocales')
  ), [config])
  const currentLocaleData = useMemo(() => (
    _.get(stores, currentLocale, [])
  ), [currentLocale, stores])
  const currentLocaleChecksum = useMemo(() => (
    _.get(storesChecksum, currentLocale, '')
  ), [currentLocale, storesChecksum])
  const isTodayData = useMemo(() => {
    const firstStoreOpenTime = _.get(_.first(currentLocaleData), 'todayOpenTimeSlots.0.start')
    if (_.isEmpty(firstStoreOpenTime)) return false
    const dataDate = moment(firstStoreOpenTime).format('YYYY-MM-DD')
    return today === dataDate
  }, [currentLocaleData, today])

  /**
   * apiFetchStores
   * api call to fetch category
   */
  function apiFetchStores(page = 1) {
    return fetchStores({
      includes: [
        'address',
        'banner_images',
        'description',
        'meta',
        'today_open_time_slots',
        'vending_commerce_channel',
      ].join(','),
      page,
    })
  }

  /**
   * fetchAllStore
   * fetch all stores data
   */
  const fetchAllStore = () => {
    return new Promise((resolve, reject) => {
      let allStores = []
      let cacheChecksum = ''
      const _fetchStore = (nextPage) => {
        const fetchNext = _.isFunction(nextPage) ? nextPage : apiFetchStores
        fetchNext()
          .then(({ stores: newStores, next, cacheChecksum: newCacheChecksum }) => {
            allStores = [
              ...allStores,
              ...newStores,
            ]
            if (_.isEmpty(cacheChecksum)) {
              cacheChecksum = `${today}|${newCacheChecksum}`
            }
            if (_.isFunction(next)) {
              _fetchStore(next)
            } else {
              resolve({ stores: allStores, cacheChecksum })
            }
          })
          .catch(reject)
      }

      _fetchStore()
    })
  }

  /**
   * handleFetchStores
   * get stores for caching, will be used for making menu
   */
  async function handleFetchStores() {
    // use stores in cache first
    if (
      !_.isEmpty(currentLocaleData) &&
      currentLocaleChecksum === cacheChecksumValue
    ) {
      setStoresReady(true)
      setEnableRefresh(true)
      return
    }

    try {
      setEnableRefresh(false)
      const { cacheChecksum, stores: data } = await fetchAllStore()
      setStores({
        ..._.pick(stores, availableLocales),
        [currentLocale]: [
          ...data
        ]
      })
      setStoresReady(true)
      setEnableRefresh(true)
      setStoresChecksum({
        ..._.pick(storesChecksum, availableLocales),
        [currentLocale]: cacheChecksum
      })
    } catch (error) {
      setEnableRefresh(true)
      console.warn('fail to load stores for cache: ', error)
    }
  }

  /**
   * handleRefreshStoresCache
   * refresh departments cache when checksum is changed
   */
  async function handleRefreshStoresCache() {
    // when it is not required to update cache, skip the process
    if (!enableRefresh || (currentLocaleChecksum === cacheChecksumValue && isTodayData)) {
      if (_.isEmpty(currentLocaleChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setStoresChecksum({
          ..._.pick(storesChecksum, availableLocales),
          [currentLocale]: cacheChecksumValue
        })
      }
      return
    }

    try {
      // start refreshing cache data
      setEnableRefresh(false)
      const { cacheChecksum, stores: data } = await fetchAllStore()
      setStores({
        ..._.pick(stores, availableLocales),
        [currentLocale]: [
          ...data
        ]
      })
      setStoresChecksum({
        ..._.pick(storesChecksum, availableLocales),
        [currentLocale]: cacheChecksum
      })
    } catch (error) {
      console.warn('fail to refresh stores for cache', error)
    } finally {
      setEnableRefresh(true)
    }
  }

  /**
   * get all stores from omnitech API when app is mounted
   */
  useEffect(() => {
    handleFetchStores()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchStores'])
    }
  }, [])

  /**
   * update stores cache base on cache checksum
   */
  useEffect(() => {
    if (!storesReady || _.isUndefined(cacheChecksumValue)) return
    handleRefreshStoresCache()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchStores'])
    }
  }, [cacheChecksumValue, storesReady, currentLocaleChecksum, today])

  const state = {
    stores: currentLocaleData,
    storesReady,
    fetchStore,
    fetchStoreByCode,
    fetchStores,
    fetchStoreDateTimeInformation,
  };

  return (
    <StoresContext.Provider value={state}>
      {children}
    </StoresContext.Provider>
  )
};
